import {
	DndContext,
	DragEndEvent,
	MouseSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	PaginationState,
	useReactTable,
} from '@tanstack/react-table'
import { DataTableSearch } from '@/common/ui/table/data-table-search'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	DoubleArrowLeftIcon,
	DoubleArrowRightIcon,
} from '@radix-ui/react-icons'
import { ChevronLeftIcon, ChevronRightIcon, GripVertical } from 'lucide-react'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { DataTableSkeleton } from '@/common/ui/table/data-table-skeleton'
import { DataTableResetFilter } from '../data-table-reset-filter'

interface SortableTableProps<TData> {
	data: TData[]
	columns: ColumnDef<TData>[]
	searchPlaceholder?: string
	onOrderChange: (
		newData: TData[],
		movedItem: TData,
		newIndex: number,
	) => Promise<void>
	SortableRowComponent: React.ComponentType<{
		row: any
	}>
	totalItems: number
	pageSizeOptions?: number[]
	isLoading?: boolean
	/** เมื่อ true ไม่แสดงคอลัมน์ลากและปิดการสลับลำดับ */
	disableDrag?: boolean
}

export function SortableTable<TData extends { id: string | number }>({
	data: initialData,
	columns,
	searchPlaceholder,
	onOrderChange,
	SortableRowComponent,
	totalItems,
	pageSizeOptions = [10, 20, 30, 40, 50],
	isLoading = false,
	disableDrag = false,
}: SortableTableProps<TData>) {
	const [data, setData] = useState(initialData)
	const { pageSize, setPageSize, currentPage, setCurrentPage } =
		useTablePagination()

	const paginationState = {
		pageIndex: currentPage - 1,
		pageSize: pageSize,
	}

	const pageCount = Math.ceil(totalItems / pageSize)

	const handlePaginationChange = (
		updaterOrValue:
			| PaginationState
			| ((old: PaginationState) => PaginationState),
	) => {
		const pagination =
			typeof updaterOrValue === 'function'
				? updaterOrValue(paginationState)
				: updaterOrValue

		setCurrentPage(pagination.pageIndex + 1)
		setPageSize(pagination.pageSize)
	}

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 200,
				tolerance: 5,
			},
		}),
	)

	const table = useReactTable({
		data,
		columns: disableDrag
			? columns
			: [
					{
						id: 'drag',
						cell: () => <GripVertical className="h-4 w-4 cursor-grab" />,
						enableSorting: false,
					},
					...columns,
				],
		pageCount: pageCount,
		state: {
			pagination: paginationState,
		},
		onPaginationChange: handlePaginationChange,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		manualPagination: true,
		manualFiltering: true,
	})

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const oldIndex = data.findIndex((item: any) => item.id === active.id)
		const newIndex = data.findIndex((item: any) => item.id === over.id)

		const newData = arrayMove(data, oldIndex, newIndex).map(
			(item: any, index) => ({
				...item,
				order: index + 1,
			}),
		)
		setData(newData)

		const movedItem = newData[newIndex]
		if (movedItem) {
			try {
				await onOrderChange(newData, movedItem, newIndex)
			} catch {
				setData(data)
			}
		}
	}

	useEffect(() => {
		setData(initialData)
	}, [initialData])

	const columnCount = disableDrag ? columns.length : columns.length + 1

	return (
		<div className="space-y-4">
			{searchPlaceholder && (
				<div className="flex items-center space-x-2">
					<DataTableSearch placeHolder={searchPlaceholder} />
					<DataTableResetFilter />
				</div>
			)}
			<ScrollArea className="h-[calc(80dvh-220px)] rounded-md border md:h-[calc(90dvh-240px)]">
				{disableDrag ? (
					<Table className="relative">
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableHead key={header.id}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
										</TableHead>
									))}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<DataTableSkeleton
									columnCount={columnCount}
									rowCount={pageSize}
								/>
							) : table.getRowModel().rows?.length ? (
								table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										data-state={row.getIsSelected() && 'selected'}
									>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={columnCount} className="h-24 text-center">
										ไม่มีข้อมูล.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				) : (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<Table className="relative">
							<TableHeader>
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<TableHead key={header.id}>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column.columnDef.header,
															header.getContext(),
														)}
											</TableHead>
										))}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								<SortableContext
									items={data.map((item: any) => item.id)}
									strategy={verticalListSortingStrategy}
								>
									{isLoading ? (
										<DataTableSkeleton
											columnCount={columnCount}
											rowCount={pageSize}
										/>
									) : table.getRowModel().rows?.length ? (
										table
											.getRowModel()
											.rows.map((row) => (
												<SortableRowComponent key={row.id} row={row} />
											))
									) : (
										<TableRow>
											<TableCell
												colSpan={columnCount}
												className="h-24 text-center"
											>
												ไม่มีข้อมูล.
											</TableCell>
										</TableRow>
									)}
								</SortableContext>
							</TableBody>
						</Table>
					</DndContext>
				)}
				<ScrollBar orientation="horizontal" />
			</ScrollArea>

			<div className="flex flex-col items-center justify-end gap-2 space-x-2 py-4 sm:flex-row">
				<div className="flex w-full items-center justify-between">
					<div className="flex-1 text-sm text-muted-foreground">
						{totalItems > 0 ? (
							<>
								Showing{' '}
								{paginationState.pageIndex * paginationState.pageSize + 1} to{' '}
								{Math.min(
									(paginationState.pageIndex + 1) * paginationState.pageSize,
									totalItems,
								)}{' '}
								of {totalItems} entries
							</>
						) : (
							'No entries found'
						)}
					</div>
					<div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
						<div className="flex items-center space-x-2">
							<p className="whitespace-nowrap text-sm font-medium">
								Rows per page
							</p>
							<Select
								value={`${paginationState.pageSize}`}
								onValueChange={(value) => {
									table.setPageSize(Number(value))
								}}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue placeholder={paginationState.pageSize} />
								</SelectTrigger>
								<SelectContent side="top">
									{pageSizeOptions.map((pageSize) => (
										<SelectItem key={pageSize} value={`${pageSize}`}>
											{pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</div>
				<div className="flex w-full items-center justify-between gap-2 sm:justify-end">
					<div className="flex w-[150px] items-center justify-center text-sm font-medium">
						{totalItems > 0 ? (
							<>
								Page {paginationState.pageIndex + 1} of {table.getPageCount()}
							</>
						) : (
							'No pages'
						)}
					</div>
					<div className="flex items-center space-x-2">
						<Button
							aria-label="Go to first page"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(0)}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to previous page"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to next page"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to last page"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(table.getPageCount() - 1)}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
